import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
  Chip,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { useDataStore } from "../dataStore";
import { toMediaUrl, withImageSize } from "../api";
import { brandThemeCssVars } from "../themeTokens";
import PageStateCard from "../components/PageStateCard";
import BrandedImageFallback from "../components/BrandedImageFallback";

function getMainImage(loc) {
  const imgs = loc?.locationImage_location || [];
  const main = imgs.find((x) => x.isMain) || imgs[0];
  return main?.image ? toMediaUrl(main.image) : "";
}

const safeStr = (v) => (v ?? "").toString();

const byName = (a, b) =>
  safeStr(a).localeCompare(safeStr(b), "ro", { sensitivity: "base" });

function LocationGridSkeleton() {
  return (
    <Grid container spacing={{ xs: 1.5, md: 2 }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid item xs={12} md={6} key={index}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              overflow: "hidden",
              borderRadius: "18px",
              border: "1px solid rgba(124,45,18,0.08)",
              bgcolor: "var(--brand-surface)",
              boxShadow: "0 12px 30px rgba(33,20,12,0.07)",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
                <Skeleton
                  variant="rounded"
                  sx={{
                    width: { xs: "100%", lg: 210 },
                    height: { xs: 190, sm: 230, md: 250, lg: 176 },
                    borderRadius: 0,
                    flexShrink: 0,
                    bgcolor: "rgba(254,215,170,0.34)",
                  }}
                />

                <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1.75, sm: 2 }, pl: { lg: 0 } }}>
                  <Stack spacing={1}>
                    <Skeleton width="64%" height={25} />
                    <Skeleton width="92%" />
                    <Skeleton width="72%" />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
                      <Skeleton variant="rounded" width={96} height={28} sx={{ borderRadius: "8px" }} />
                      <Skeleton variant="rounded" width={30} height={30} sx={{ borderRadius: "8px" }} />
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

export default function Locations() {
  const {
    loading,
    error,
    locations,
    brandTheme,
    t,
    retryCore,
  } = useDataStore();
  const c = brandTheme;
  const compactViewport = useMediaQuery("(max-width:899.95px)");
  const [q, setQ] = useState("");
  const [heroRandomSeed] = useState(() => Math.random());

  const filteredSorted = useMemo(() => {
    const query = q.trim().toLowerCase();

    return (locations || [])
      .filter((l) => {
        if (!query) return true;

        const city = l?.address?.city?.name || "";
        const street = l?.address?.street || "";
        const number = l?.address?.number || "";
        const addr = `${street} ${number}`.trim();

        return (
          safeStr(l.name).toLowerCase().includes(query) ||
          safeStr(l.description).toLowerCase().includes(query) ||
          safeStr(city).toLowerCase().includes(query) ||
          safeStr(addr).toLowerCase().includes(query) ||
          safeStr(l.phone).toLowerCase().includes(query)
        );
      })
      .slice()
      .sort((a, b) => byName(a?.name, b?.name));
  }, [q, locations]);

  const heroImage = useMemo(() => {
    const candidates = (locations || [])
      .map(getMainImage)
      .filter(Boolean);

    if (!candidates.length) return "";

    const randomIndex = Math.min(
      candidates.length - 1,
      Math.max(0, Math.floor(heroRandomSeed * candidates.length))
    );
    return candidates[randomIndex] || "";
  }, [locations, heroRandomSeed]);

  const cityCount = useMemo(() => {
    return new Set(
      (locations || [])
        .map((loc) => loc?.address?.city?.name)
        .filter(Boolean)
    ).size;
  }, [locations]);

  return (
    <Stack
      spacing={{ xs: 2.5, md: 3 }}
      sx={{ ...brandThemeCssVars(c), pb: { xs: 12, md: 0 } }}
    >
      {/* Editorial header */}
      <Box
        sx={{
          overflow: "hidden",
          borderRadius: { xs: "18px", md: "24px" },
          position: "relative",
          bgcolor: c.surface,
          border: "1px solid rgba(15,118,110,0.14)",
          boxShadow: "0 24px 64px rgba(33,20,12,0.13)",
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: `linear-gradient(135deg, ${c.background} 0%, color-mix(in srgb, ${c.cta} 14%, ${c.surface}) 100%)`,
            }}
          >
            <Stack
              spacing={1.5}
              sx={{
                p: { xs: 2.75, sm: 4, md: 5 },
                minHeight: { xs: 290, md: 400 },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: c.onPrimary,
                    background: c.primary,
                    border: "1px solid rgba(124,45,18,0.10)",
                    boxShadow: "0 12px 26px rgba(247,181,0,0.18)",
                    flexShrink: 0,
                  }}
                >
                  <StorefrontRoundedIcon />
                </Box>

                <Typography
                  sx={{
                    color: c.accent,
                    fontSize: 13,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {t("locations.find")}
                </Typography>
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontWeight: 950,
                  color: c.text,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.02,
                  fontSize: { xs: 38, sm: 52, md: 62 },
                  maxWidth: 620,
                }}
              >
                {t("locations.heroTitle")}
              </Typography>

              <Typography
                sx={{
                  color: c.muted,
                  lineHeight: 1.65,
                  maxWidth: 560,
                  fontSize: { xs: 15, md: 17 },
                }}
              >
                {t("locations.heroSubtitle")}
              </Typography>
            </Stack>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              position: "relative",
              minHeight: { xs: 250, sm: 320, md: 400 },
              bgcolor: c.background,
            }}
          >
            {heroImage ? (
              <Box
                component="img"
                src={withImageSize(heroImage, "l")}
                alt=""
                loading="eager"
                decoding="async"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <BrandedImageFallback
                icon={<StorefrontRoundedIcon />}
                label={t("common.noImage")}
                brandTheme={c}
              />
            )}

            {heroImage ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, rgba(23,17,12,0.12), rgba(23,17,12,0.02))",
                  pointerEvents: "none",
                }}
              />
            ) : null}
          </Grid>
        </Grid>
      </Box>

      {error ? (
        <PageStateCard
          tone="error"
          title={t("common.loadErrorTitle")}
          description={t("common.loadError")}
          actionLabel={t("common.retry")}
          onAction={retryCore}
          brandTheme={c}
        />
      ) : null}

      {/* Search */}
      <Box
        data-testid="location-discovery-toolbar"
        sx={{
          display: error ? "none" : "block",
          position: compactViewport ? "fixed" : "sticky",
          top: compactViewport
            ? "auto"
            : "calc(var(--app-header-height, 64px) + 8px)",
          bottom: compactViewport ? 0 : "auto",
          left: compactViewport ? 0 : "auto",
          right: compactViewport ? 0 : "auto",
          zIndex: compactViewport ? 1190 : 20,
          boxSizing: "border-box",
          p: compactViewport ? 1 : 1.25,
          pb: compactViewport
            ? "calc(8px + env(safe-area-inset-bottom))"
            : 1.25,
          borderRadius: compactViewport ? "16px 16px 0 0" : "16px",
          bgcolor: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(67,20,7,0.12)",
          borderBottom: compactViewport
            ? 0
            : "1px solid rgba(67,20,7,0.12)",
          boxShadow: "0 10px 28px rgba(33,20,12,0.10)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("locations.searchLong")}
              inputProps={{ "aria-label": t("locations.searchLong") }}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "12px",
                  bgcolor: c.background,
                  fontWeight: 700,
                  minHeight: 44,
                },
              }}
            />
          </Box>

          {q.trim() ? (
            <Chip
              icon={<CloseRoundedIcon />}
              label={t("common.reset")}
              clickable
              onClick={() => setQ("")}
              sx={{
                minHeight: 44,
                flexShrink: 0,
                fontWeight: 850,
                color: c.accent,
                bgcolor: c.background,
                border: "1px solid rgba(67,20,7,0.12)",
                "& .MuiChip-icon": { color: c.accent },
              }}
            />
          ) : null}
        </Stack>
      </Box>

      {!loading && !error ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.5}
          justifyContent="space-between"
          sx={{ px: { xs: 0.25, sm: 0.5 }, color: c.muted }}
        >
          <Typography variant="body2" sx={{ fontWeight: 750 }}>
            {filteredSorted.length} {t("locations.displayed")}
          </Typography>
          {cityCount ? (
            <Typography variant="body2">
              {cityCount}{" "}
              {cityCount === 1
                ? t("locations.cityOne")
                : t("locations.cityMany")}
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      <Box sx={{ display: error ? "none" : "block", minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          {/* Empty state */}
          {!loading && !error && !filteredSorted.length ? (
            <PageStateCard
              title={t("locations.emptyTitle")}
              description={t("locations.emptyDesc")}
              actionLabel={q.trim() ? t("locations.resetSearch") : ""}
              onAction={q.trim() ? () => setQ("") : undefined}
              brandTheme={c}
            />
          ) : null}

          {/* Locations grid */}
          {loading ? (
            <LocationGridSkeleton />
          ) : !error ? (
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
        {filteredSorted.map((l) => {
          const city = l?.address?.city?.name || "";
          const street = l?.address?.street || "";
          const number = l?.address?.number || "";
          const addr = `${street} ${number}`.trim();
          const addressLine = [city, addr].filter(Boolean).join(", ");
          const img = getMainImage(l);

          return (
            <Grid item xs={12} md={6} key={l.iref}>
              <Card
                component={RouterLink}
                to={`/locations/${l.iref}`}
                elevation={0}
                sx={{
                  height: "100%",
                  textDecoration: "none",
                  color: "inherit",
                  overflow: "hidden",
                  borderRadius: "18px",
                  border: "1px solid rgba(124,45,18,0.08)",
                  bgcolor: c.surface,
                  boxShadow: "0 14px 34px rgba(33,20,12,0.08)",
                  transition:
                    "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 20px 46px rgba(33,20,12,0.14)",
                    borderColor: "rgba(234,88,12,0.24)",
                  },
                  "&:focus-visible": {
                    outline: `3px solid ${c.primary}`,
                    outlineOffset: 3,
                  },
                  "&:hover .premium-card-media": {
                    transform: "scale(1.035)",
                    filter: "saturate(1.05)",
                  },
                  "&:hover .premium-card-arrow": {
                    transform: "translateX(3px)",
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                    "&:hover": {
                      transform: "none",
                    },
                    "&:hover .premium-card-media, &:hover .premium-card-arrow": {
                      transform: "none",
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
                    <Box
                      sx={{
                        position: "relative",
                        width: { xs: "100%", lg: 210 },
                        height: { xs: 190, sm: 230, md: 250, lg: 176 },
                        overflow: "hidden",
                        bgcolor: c.background,
                        flexShrink: 0,
                      }}
                    >
                      {img ? (
                        <Box
                          component="img"
                          className="premium-card-media"
                          src={withImageSize(img, "m")}
                          alt={l.name}
                          loading="lazy"
                          decoding="async"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            transition: "transform 220ms ease, filter 220ms ease",
                          }}
                        />
                      ) : (
                        <BrandedImageFallback
                          className="premium-card-media"
                          icon={<StorefrontRoundedIcon />}
                          label={t("common.noImage")}
                          brandTheme={c}
                          sx={{ transition: "transform 220ms ease, filter 220ms ease" }}
                        />
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(180deg, rgba(17,17,17,0.02) 40%, rgba(17,17,17,0.40) 100%)",
                          pointerEvents: "none",
                        }}
                      />

                      {city ? (
                        <Chip
                          size="small"
                          label={city}
                          sx={{
                            position: "absolute",
                            left: 10,
                            bottom: 10,
                            maxWidth: "calc(100% - 20px)",
                            color: "#111111",
                            bgcolor: "rgba(255,255,255,0.92)",
                            border: "1px solid rgba(255,255,255,0.36)",
                            fontWeight: 850,
                          }}
                        />
                      ) : null}
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        p: { xs: 2, sm: 2.25 },
                        pl: { lg: 0 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 950,
                          color: c.text,
                          lineHeight: 1.22,
                          fontSize: 17,
                        }}
                      >
                        {l.name}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="flex-start"
                        sx={{ mt: 1 }}
                      >
                        <PlaceOutlinedIcon
                          fontSize="small"
                          sx={{ color: c.accent, mt: "2px", flexShrink: 0 }}
                        />

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.45 }}
                        >
                          {addressLine || t("common.unavailableAddress")}
                        </Typography>
                      </Stack>

                      {l.phone ? (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ mt: 0.75 }}
                        >
                          <PhoneOutlinedIcon
                            fontSize="small"
                            sx={{ color: c.accent, flexShrink: 0 }}
                          />

                          <Typography variant="body2" color="text.secondary">
                            {l.phone}
                          </Typography>
                        </Stack>
                      ) : null}

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ mt: 1.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: c.accent,
                            fontWeight: 850,
                          }}
                        >
                          {t("common.viewDetails")}
                        </Typography>
                        <ArrowForwardRoundedIcon
                          className="premium-card-arrow"
                          sx={{
                            color: c.accent,
                            fontSize: 19,
                            transition: "transform 160ms ease",
                          }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
          </Grid>
          ) : null}
        </Box>
      </Box>
    </Stack>
  );
}
